import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fleetService } from '../services/fleetService';
import { Search, MapPin, Calendar, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils/cn';

function formatNok(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return '0.00';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Supabase may return nested ride_commissions as object or single-element array */
function getRideCommissionRow(ride) {
  const rc = ride?.ride_commissions;
  if (!rc) return null;
  return Array.isArray(rc) ? rc[0] ?? null : rc;
}

const statusColors = {
  pending: 'bg-warning-light dark:bg-warning-dark text-warning border-warning/20',
  accepted: 'bg-info-light dark:bg-info-dark text-info border-info/20',
  arrived: 'bg-primary-light dark:bg-primary-dark text-primary border-primary/20',
  picked_up: 'bg-primary-light dark:bg-primary-dark text-primary border-primary/20',
  in_progress: 'bg-primary-light dark:bg-primary-dark text-primary border-primary/20',
  completed: 'bg-success-light dark:bg-success-dark text-success border-success/20',
  cancelled: 'bg-danger-light dark:bg-danger-dark text-danger border-danger/20',
};

export default function Rides() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['fleet-rides', statusFilter, page, search],
    queryFn: () => {
      return fleetService.getRides({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        page,
        limit: 10,
        search: search || undefined,
      }).then((res) => res.data);
    },
  });

  const rides = data?.data || [];
  const pagination = data?.pagination || {};

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-text dark:text-text-dark mb-2">Rides</h1>
          <p className="text-text-secondary dark:text-text-secondary-dark">
            View and manage all fleet rides
          </p>
        </div>
        <div className="flex gap-2">
          <button className="hidden sm:flex items-center gap-2 px-4 py-2 bg-surface dark:bg-surface-dark border border-border dark:border-border-dark rounded-lg text-sm font-medium text-text dark:text-text-dark hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark transition-colors">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-surface dark:bg-surface-dark rounded-2xl border border-border dark:border-border-dark shadow-lg overflow-hidden">
        <div className="p-4 lg:p-6 border-b border-border dark:border-border-dark bg-gradient-to-r from-surface-secondary/50 to-transparent dark:from-surface-secondary-dark/50">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-tertiary dark:text-text-tertiary-dark" />
              <input
                type="text"
                placeholder="Search rides by pickup or destination..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {['all', 'pending', 'accepted', 'in_progress', 'completed', 'cancelled'].map((status) => (
                <button
                  key={status}
                  onClick={() => {
                    setStatusFilter(status);
                    setPage(1);
                  }}
                  className={cn(
                    'px-4 py-2 rounded-xl font-medium transition-all text-sm capitalize',
                    statusFilter === status
                      ? 'bg-primary text-white shadow-lg'
                      : 'bg-surface-secondary dark:bg-surface-secondary-dark text-text-secondary dark:text-text-secondary-dark hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark hover:scale-105'
                  )}
                >
                  {status.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Rides List */}
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
            <div className="text-text-secondary dark:text-text-secondary-dark">Loading rides...</div>
          </div>
        ) : rides.length === 0 ? (
          <div className="p-12 text-center">
            <MapPin className="w-16 h-16 text-text-tertiary dark:text-text-tertiary-dark mx-auto mb-4" />
            <div className="text-text-secondary dark:text-text-secondary-dark">No rides found</div>
          </div>
        ) : (
          <>
            <div className="divide-y divide-border dark:divide-border-dark">
              {rides.map((ride) => (
                <div
                  key={ride.id}
                  className="p-4 lg:p-6 hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark cursor-pointer transition-all duration-200 group"
                  onClick={() => navigate(`/rides/${ride.id}`)}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className={cn('px-3 py-1 rounded-full text-xs font-semibold border', statusColors[ride.status] || statusColors.pending)}>
                          {ride.status?.replace('_', ' ').toUpperCase()}
                        </span>
                        <span className="text-sm text-text-secondary dark:text-text-secondary-dark font-mono">
                          #{ride.id?.slice(0, 8)}
                        </span>
                        {ride.created_at && (
                          <span className="text-xs text-text-tertiary dark:text-text-tertiary-dark flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(ride.created_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2 text-sm text-text dark:text-text-dark">
                          <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="flex-1">{ride.pickup_address || 'N/A'}</span>
                        </div>
                        <div className="flex items-start gap-2 text-sm text-text-secondary dark:text-text-secondary-dark">
                          <MapPin className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                          <span className="flex-1">{ride.destination_address || 'N/A'}</span>
                        </div>
                      </div>
                      {ride.driver && (
                        <div className="text-sm text-text-secondary dark:text-text-secondary-dark">
                          Driver: <span className="font-semibold text-text dark:text-text-dark">{ride.driver.first_name} {ride.driver.last_name}</span>
                        </div>
                      )}
                    </div>
                    <div className="lg:text-right flex-shrink-0 space-y-2">
                      <div>
                        <div className="text-2xl font-bold text-text dark:text-text-dark mb-1">
                          NOK {formatNok(ride.actual_fare ?? ride.estimated_fare ?? 0)}
                        </div>
                        <div className="text-xs text-text-secondary dark:text-text-secondary-dark">
                          {ride.actual_fare != null ? 'Fare (final)' : 'Fare (estimated)'}
                        </div>
                      </div>
                      {(() => {
                        const comm = getRideCommissionRow(ride);
                        if (!comm || comm.commission_amount == null) return null;
                        return (
                          <div className="pt-1 border-t border-border dark:border-border-dark lg:border-0 lg:pt-0">
                            <div className="text-lg font-semibold text-text dark:text-text-dark">
                              NOK {formatNok(comm.commission_amount)}
                            </div>
                            <div className="text-xs text-text-secondary dark:text-text-secondary-dark">
                              Platform commission
                            </div>
                            {comm.fleet_earnings != null && (
                              <div className="text-xs text-text-tertiary dark:text-text-tertiary-dark mt-1">
                                Fleet earnings NOK {formatNok(comm.fleet_earnings)}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.total > 0 && (
              <div className="p-4 lg:p-6 border-t border-border dark:border-border-dark flex flex-col sm:flex-row items-center justify-between gap-4 bg-surface-secondary/30 dark:bg-surface-secondary-dark/30">
                <div className="text-sm text-text-secondary dark:text-text-secondary-dark">
                  Showing <span className="font-semibold text-text dark:text-text-dark">{rides.length}</span> of{' '}
                  <span className="font-semibold text-text dark:text-text-dark">{pagination.total || 0}</span> rides
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-surface dark:bg-surface-dark border border-border dark:border-border-dark rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark transition-colors text-sm font-medium"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!pagination.pages || page >= pagination.pages}
                    className="px-4 py-2 bg-primary text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-dark transition-colors text-sm font-medium shadow-lg"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
